import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${BACKEND_URL}/api`;

// Live Tracker Carousel Component
function LiveTrackerCarousel({ userStats, currentUserIndex }) {
  const [activeTab, setActiveTab] = useState('Active');  // Active or Closed
  const currentUser = userStats[currentUserIndex];

  // Display carousel with LIVE P&L tracking
  if (!currentUser) return null;

  // Get current date
  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div style={{
      marginBottom: '3rem',
      padding: '2rem',
      background: currentUser.total_pnl >= 0 
        ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
        : 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
      borderRadius: '16px',
      border: currentUser.total_pnl >= 0 ? '3px solid #10b981' : '3px solid #ef4444',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: '#64748b',
          marginBottom: '0.5rem',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          REAL USER PERFORMANCE
        </div>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#1e293b'
        }}>
          User #{currentUser.user_id}
        </div>
        <div style={{
          fontSize: '0.875rem',
          color: '#64748b',
          marginTop: '0.25rem'
        }}>
          {currentDate}
        </div>
      </div>

      {/* LIVE P&L Box */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            color: '#64748b', 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            LIVE Total Profit & Loss
            <span style={{ 
              fontSize: '0.625rem', 
              background: '#3b82f6', 
              color: 'white', 
              padding: '0.125rem 0.5rem', 
              borderRadius: '4px', 
              fontWeight: '700' 
            }}>LIVE</span>
          </div>
          <div style={{
            fontSize: '3rem',
            fontWeight: '800',
            color: currentUser.unrealized_pnl >= 0 ? '#10b981' : '#ef4444',
            marginBottom: '0.75rem'
          }}>
            {/* 🚨 CRITICAL: Show ONLY unrealized P&L from active trades */}
            {currentUser.unrealized_pnl >= 0 ? '+' : '-'}${Math.abs(currentUser.unrealized_pnl).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', fontWeight: '500' }}>
            From {currentUser.active_count} active {currentUser.active_count === 1 ? 'trade' : 'trades'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem', fontWeight: '600' }}>
            {/* 🚨 TODAY'S stats only (not cumulative) */}
            {currentUser.daily_wins || 0} wins • {currentUser.daily_losses || 0} losses • {currentUser.active_count} active • {currentUser.daily_win_rate || 0}% win rate
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        borderBottom: '2px solid rgba(255,255,255,0.3)',
        paddingBottom: '0.5rem'
      }}>
        <button
          onClick={() => setActiveTab('Active')}
          style={{
            padding: '0.5rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            background: activeTab === 'Active' ? '#10b981' : '#e2e8f0',
            color: activeTab === 'Active' ? 'white' : '#64748b',
            transition: 'all 0.2s'
          }}
        >
          Active
        </button>
        <button
          onClick={() => setActiveTab('Closed')}
          style={{
            padding: '0.5rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            background: activeTab === 'Closed' ? '#10b981' : '#e2e8f0',
            color: activeTab === 'Closed' ? 'white' : '#64748b',
            transition: 'all 0.2s'
          }}
        >
          Closed
        </button>
      </div>

      {/* Active Tab - Show Active Trades */}
      {activeTab === 'Active' && (
        <div>
          {currentUser.active_trades && currentUser.active_trades.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              {currentUser.active_trades.map((trade, index) => (
                <div key={index} style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: trade.status === 'profit' ? '2px solid #10b981' : trade.status === 'drawdown' ? '2px solid #ef4444' : '2px solid #94a3b8',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    fontSize: '1rem',
                    color: '#1e293b'
                  }}>
                    {trade.pair}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>
                    Live P&L: {trade.current_pnl >= 0 ? '+' : '-'}${Math.abs(trade.current_pnl).toFixed(2)}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    background: trade.status === 'profit' ? '#d1fae5' : trade.status === 'drawdown' ? '#fee2e2' : '#f1f5f9',
                    color: trade.status === 'profit' ? '#065f46' : trade.status === 'drawdown' ? '#991b1b' : '#475569',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {trade.status === 'profit' ? 'In Profit' : trade.status === 'drawdown' ? 'In Drawdown' : 'Active'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#64748b'
            }}>
              No active trades
            </div>
          )}
        </div>
      )}

      {/* Closed Tab - Show Closed Trades */}
      {activeTab === 'Closed' && (
        <div>
          {currentUser.closed_trades && currentUser.closed_trades.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              {currentUser.closed_trades.map((trade, index) => (
                <div key={index} style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: trade.outcome === 'WON' ? '2px solid #10b981' : '2px solid #ef4444',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    fontSize: '1rem',
                    color: '#1e293b'
                  }}>
                    {trade.pair}
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: trade.outcome === 'WON' ? '#10b981' : '#ef4444', 
                    fontWeight: '600' 
                  }}>
                    Final P&L: {trade.pnl >= 0 ? '+' : '-'}${Math.abs(trade.pnl).toFixed(2)}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    background: trade.outcome === 'WON' ? '#d1fae5' : '#fee2e2',
                    color: trade.outcome === 'WON' ? '#065f46' : '#991b1b',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {trade.outcome === 'WON' ? '✅ Won' : '❌ Lost'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#64748b'
            }}>
              No closed trades
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [liveStats, setLiveStats] = useState({
    win_rate: 0,
    total_profit: 0,
    total_trades: 0,
    number_of_users: 1,
    display_period: '24h'
  });
  const [loading, setLoading] = useState(true);
  
  // User Stats Carousel State
  const [userStats, setUserStats] = useState([]);
  const [currentUserIndex, setCurrentUserIndex] = useState(0);

  useEffect(() => {
    fetchLiveStats();
    fetchUserStatsCarousel();
    // Refresh both platform stats AND user carousel data every 60 seconds
    const statsInterval = setInterval(fetchLiveStats, 60000);
    const carouselDataInterval = setInterval(fetchUserStatsCarousel, 60000);
    return () => {
      clearInterval(statsInterval);
      clearInterval(carouselDataInterval);
    };
  }, []);
  
  // Rotate through user stats every 5 seconds
  useEffect(() => {
    if (userStats.length > 0) {
      const carouselInterval = setInterval(() => {
        setCurrentUserIndex((prev) => (prev + 1) % userStats.length);
      }, 5000); // 5 seconds per user
      return () => clearInterval(carouselInterval);
    }
  }, [userStats]);

  const fetchLiveStats = async () => {
    try {
      const response = await axios.get(`${API}/public/platform-stats`);
      if (response && response.data && response.data.success) {
        setLiveStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching live stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUserStatsCarousel = async () => {
    try {
      console.log('🔄 Fetching user stats from:', `${API}/public/user-stats-carousel`);
      const response = await axios.get(`${API}/public/user-stats-carousel`);
      console.log('📦 API Response:', response);
      if (response && response.data && response.data.success && response.data.user_stats && response.data.user_stats.length > 0) {
        setUserStats(response.data.user_stats);
        console.log('✅ User stats loaded:', response.data.user_stats.length, 'users', response.data.user_stats);
      } else {
        console.log('⚠️ No user stats in response');
      }
    } catch (error) {
      console.error('❌ Error fetching user stats carousel:', error);
    }
  };

  const handleRequestInvite = () => {
    navigate('/request-invite');
  };

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features-section');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      color: '#0f172a',
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Hero Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        {/* Title */}
        <h1 style={{
          fontSize: '4rem',
          fontWeight: '900',
          marginBottom: '1.5rem',
          background: 'linear-gradient(135deg, #1e40af 0%, #0369a1 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          lineHeight: '1.2',
          fontFamily: "'Montserrat', 'Arial Black', sans-serif",
          letterSpacing: '-0.02em'
        }}>
          Trade Blindfolded
        </h1>

        {/* Slogan */}
        <p style={{
          fontSize: '1.75rem',
          color: '#475569',
          marginBottom: '3rem',
          fontStyle: 'italic',
          fontWeight: '400'
        }}>
          Trading so simple, you can do it blindfolded
        </p>

        {/* Live Stats Bar */}
        <div style={{
          marginBottom: '3rem',
          padding: '2rem',
          background: 'white',
          borderRadius: '16px',
          border: '2px solid #bae6fd',
          boxShadow: '0 4px 12px rgba(14, 165, 233, 0.1)'
        }}>
          {/* Top Row - Win Rate and P/L */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981' }}>
                {loading ? '...' : `${liveStats.win_rate}%`}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', fontWeight: '600' }}>
                🎯 Win Rate
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem', fontStyle: 'italic' }}>
                {liveStats.display_period === '30d' ? 'Past 24 hours' : 'Past 24 hours'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '2.5rem', 
                fontWeight: '700', 
                color: loading ? '#64748b' : (liveStats.total_profit >= 0 ? '#10b981' : '#ef4444')
              }}>
                {loading ? '...' : `${liveStats.total_profit >= 0 ? '+' : ''}$${liveStats.total_profit.toLocaleString()}`}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', fontWeight: '600' }}>
                💰 Net P/L
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem', fontStyle: 'italic' }}>
                {liveStats.display_period === '30d' ? 'Past 24 hours' : 'Past 24 hours'}
              </div>
            </div>
          </div>
          
          {/* Bottom Row - Number of Users (Centered) */}
          <div style={{ 
            textAlign: 'center',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#3b82f6' }}>
              {loading ? '...' : liveStats.number_of_users || 1}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', fontWeight: '600' }}>
              👥 Number of Users
            </div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem', fontStyle: 'italic' }}>
              Platform traders
            </div>
          </div>
        </div>

        {/* Live Tracker - User Performance Carousel */}
        {/* 🚨 CRITICAL: Hide carousel during rollover when no active trades exist */}
        {userStats.length > 0 && userStats.some(user => user.active_count > 0) ? (
          <LiveTrackerCarousel userStats={userStats} currentUserIndex={currentUserIndex} />
        ) : userStats.length > 0 && userStats.every(user => user.active_count === 0) ? (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            color: '#10b981', 
            fontSize: '1rem',
            fontWeight: '600',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
            borderRadius: '12px',
            border: '2px solid rgba(16, 185, 129, 0.2)'
          }}>
            🟢 Rollover Complete - All trades closed for ML training. Calculator reopens at 23:05 UTC.
          </div>
        ) : (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
            Loading user performance data...
          </div>
        )}

        {/* Login Section for Existing Users */}
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '12px',
          border: '2px solid #bae6fd',
          boxShadow: '0 4px 12px rgba(14, 165, 233, 0.1)',
          maxWidth: '500px',
          margin: '0 auto 2rem'
        }}>
          <p style={{
            fontSize: '1rem',
            color: '#475569',
            marginBottom: '1rem',
            fontWeight: '600'
          }}>
            Already have access?
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '1rem 2.5rem',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#7c3aed',
              background: 'transparent',
              border: '2px solid #7c3aed',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f3e8ff';
              e.target.style.borderColor = '#a855f7';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = '#7c3aed';
            }}
          >
            🔐 Login to Your Account
          </button>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleRequestInvite}
          style={{
            padding: '1.25rem 3rem',
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#fff',
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)',
            transition: 'all 0.3s ease',
            marginBottom: '4rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 15px 40px rgba(59, 130, 246, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 10px 30px rgba(59, 130, 246, 0.4)';
          }}
        >
          Request Invite
        </button>

        {/* Pulsating Down Arrow */}
        <div 
          onClick={scrollToFeatures}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '2rem',
            cursor: 'pointer',
            animation: 'pulse 2s ease-in-out infinite'
          }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
          }}
          >
            <svg 
              width="28" 
              height="28" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="white" 
              strokeWidth="3" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div 
        id="features-section"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '4rem 2rem'
        }}
      >
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: '3rem',
          color: '#0f172a'
        }}>
          What Trade Blindfolded Can Do
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {/* Feature 1 */}
          <div style={{
            padding: '2rem',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #bae6fd',
            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.1)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📈</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0ea5e9' }}>
              Advanced Market Analysis
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Sophisticated algorithms analyze market conditions, volume, momentum, and price action to recommend high-probability trades in seconds.
            </p>
          </div>

          {/* Feature 2 */}
          <div style={{
            padding: '2rem',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #bbf7d0',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#10b981' }}>
              4-Click Trading System
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Calculate trade → Confirm → Send to MT5 → Execute. No chart analysis required. Simple enough to trade blindfolded.
            </p>
          </div>

          {/* Feature 3 */}
          <div style={{
            padding: '2rem',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #fde68a',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.1)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚡</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#f59e0b' }}>
              Automated Breakout Detection
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              System scans all pairs every 30 minutes and alerts you with visual indicators when high-probability breakouts are detected.
            </p>
          </div>

          {/* Feature 4 */}
          <div style={{
            padding: '2rem',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #ddd6fe',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛡️</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#8b5cf6' }}>
              Smart Risk Management
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Pair-specific stop losses, automatic trailing stops, daily rollover protection, and intelligent position sizing keep your account safe.
            </p>
          </div>

          {/* Feature 5 */}
          <div style={{
            padding: '2rem',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #bae6fd',
            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.1)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#06b6d4' }}>
              Performance Analytics
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Track every trade's outcome, analyze success rates by strategy, and watch the system continuously improve from historical data.
            </p>
          </div>

          {/* Feature 6 */}
          <div style={{
            padding: '2rem',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #fbcfe8',
            boxShadow: '0 4px 12px rgba(236, 72, 153, 0.1)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#ec4899' }}>
              Continuous Optimization
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              The system adapts to market conditions, learns from losing trades, and continuously optimizes probability calculations for better results.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          marginBottom: '3rem',
          color: '#0f172a'
        }}>
          How It Works
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          <div>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1rem',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: '700',
              color: 'white'
            }}>
              1
            </div>
            <h4 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#0f172a' }}>Select Pair & Risk</h4>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Choose currency pair and how much you want to risk
            </p>
          </div>

          <div>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: '700',
              color: 'white'
            }}>
              2
            </div>
            <h4 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#0f172a' }}>System Analyzes Market</h4>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              System calculates optimal entry, stop loss, and take profit
            </p>
          </div>

          <div>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1rem',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: '700',
              color: 'white'
            }}>
              3
            </div>
            <h4 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#0f172a' }}>Review & Confirm</h4>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              See probability score and trade details before executing
            </p>
          </div>

          <div>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1rem',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: '700',
              color: 'white'
            }}>
              4
            </div>
            <h4 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#0f172a' }}>Execute & Track</h4>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Send to MT5 and let automated systems monitor your trade
            </p>
          </div>
        </div>

        {/* Final CTA */}
        <button
          onClick={handleRequestInvite}
          style={{
            padding: '1.25rem 3rem',
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#fff',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 15px 40px rgba(16, 185, 129, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 10px 30px rgba(16, 185, 129, 0.4)';
          }}
        >
          Get Started Today
        </button>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        borderTop: '2px solid #bae6fd',
        color: '#64748b',
        fontSize: '0.875rem',
        background: 'white'
      }}>
        <p>© 2025 Trade Blindfolded. Making trading accessible to everyone.</p>
      </div>

      {/* CSS Animation for Pulsating Arrow */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: translateY(0);
            opacity: 1;
          }
          50% {
            transform: translateY(10px);
            opacity: 0.7;
          }
        }
      `}</style>
    </div>
  );
}

export default LandingPage;
